import type { ApolloClient, ApolloQueryResult, QueryOptions } from '@apollo/client';
import { isNil } from 'es-toolkit';

import type { ResultOf } from '@savvy/deprecated-shared/gql-compat';
import { fetchQueryWithoutCaching } from '@savvy/deprecated-shared/deprecated-gql/gql';
import { graphql as tadaGraphql } from '@savvy/deprecated-shared/gql-compat';
import { LocalStorage } from '@savvy/deprecated-shared/utils/local-storage';
import { isWeb } from '@savvy/deprecated-shared/utils/platform/is-web';
import { setSalesforceAccessToken } from '@savvy/deprecated-shared/utils/salesforce';

export const CurrentUserQuery = tadaGraphql(/* GraphQL */ `
  query CurrentUser {
    currentUser {
      user {
        createdAt
        email
        phoneNumber
        firstName
        id
        lastName
        role
        addresses {
          id
        }
        confirmationStatus
        clientStage
        profileType
        profileId
        profilePicUrl
        isPrimaryClient
        otpRequiredForLogin
        personalPortfolioId
        aiExperienceOptIn
        primaryAdvisorEmail
        timezone
      }
      salesforceAccessToken
    }
  }
`);

type CurrentUser = ResultOf<typeof CurrentUserQuery>['currentUser'];
export type JWTContextUser = Partial<CurrentUser['user']>;

function handleSalesforceAccessToken(currentUser: ResultOf<typeof CurrentUserQuery>['currentUser'], shouldSync = true) {
  if (shouldSync && currentUser.salesforceAccessToken) {
    setSalesforceAccessToken(currentUser.salesforceAccessToken);
  }
}

async function currentUserQueryPromise(
  client: ApolloClient<object>,
  bypassCache?: boolean,
): Promise<ApolloQueryResult<ResultOf<typeof CurrentUserQuery>>> {
  const options: QueryOptions = { query: CurrentUserQuery };

  if (bypassCache) {
    options.fetchPolicy = 'network-only';
  }

  return await client.query(options);
}

export async function fetchCurrentUser(
  client: ApolloClient<object>,
  bypassCache?: boolean,
  salesforceSyncEnabled?: boolean,
): Promise<JWTContextUser> {
  const asyncAccessToken = await LocalStorage.getItem('accessToken');
  // This gql query used by this function requires the access token to be set in localstorage!
  if (isWeb() && typeof window !== 'undefined') {
    if (!Object.hasOwn(window.localStorage, 'accessToken')) {
      throw new Error('Access token is not found');
    }
  } else if (!isWeb() && isNil(asyncAccessToken)) {
    throw new Error('Access token is not found');
  }

  const rawData = await currentUserQueryPromise(client, bypassCache);

  handleSalesforceAccessToken(rawData.data.currentUser, salesforceSyncEnabled !== false);

  return rawData.data.currentUser.user;
}

export async function fetchCurrentUserWithoutCaching() {
  const data = await fetchQueryWithoutCaching(CurrentUserQuery);
  return data?.currentUser.user;
}
